"use client";

import { ChevronUp } from "lucide-react";
// Using simplified user type instead of next-auth
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useNavigate } from "react-router-dom";
import { toast } from "./toast";
import { LoaderIcon } from "./icons";

export function SidebarUserNav({ user }: { user: any }) {
  const navigate = useNavigate();
  const { user: authUser, userData, loading, signOut } = useAuth();
  const { setTheme, theme } = useTheme();

  // Use userData for user info since it has the complete profile
  const status = loading
    ? "loading"
    : authUser
      ? "authenticated"
      : "unauthenticated";

  // Use the userData for user info, fallback to authUser
  const currentUser = userData || authUser || user;
  const isGuest = false; // Simplified for now

  let displayName = currentUser?.email; // Fallback to email
  if (isGuest) {
    displayName = "Guest User"; // Consistent with what we set in auth.ts for anonymous users
  } else if (currentUser && currentUser.firstName && currentUser.lastName) {
    displayName = `${currentUser.firstName} ${currentUser.lastName}`;
  } else if (currentUser && currentUser.firstName) {
    displayName = currentUser.firstName;
  } else if (currentUser && currentUser.lastName) {
    displayName = currentUser.lastName;
  }

  // Avatar logic: Use email for vercel avatar if not guest. For guest, use initials or default.
  const avatarSrc =
    !isGuest && currentUser?.email
      ? `https://avatar.vercel.sh/${currentUser.email}`
      : undefined;
  const avatarFallback = isGuest
    ? "GU"
    : (
        (currentUser?.firstName?.charAt(0) ?? "") +
        (currentUser?.lastName?.charAt(0) ?? "")
      ).toUpperCase() ||
      (
        currentUser?.name?.charAt(0) ??
        currentUser?.email?.charAt(0) ??
        "U"
      ).toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {status === "loading" ? (
              <SidebarMenuButton className="data-[state=open]:bg-sidebar-accent bg-background data-[state=open]:text-sidebar-accent-foreground h-10 justify-between">
                <div className="flex flex-row gap-2">
                  <div className="size-6 bg-zinc-500/30 rounded-full animate-pulse" />
                  <span className="bg-zinc-500/30 text-transparent rounded-md animate-pulse">
                    Loading auth status
                  </span>
                </div>
                <div className="animate-spin text-zinc-500">
                  <LoaderIcon />
                </div>
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton
                data-testid="user-nav-button"
                className="data-[state=open]:bg-sidebar-accent bg-background data-[state=open]:text-sidebar-accent-foreground h-auto min-h-10 py-2"
              >
                <img
                  src={avatarSrc || `/default-avatar.png`} // Provide a fallback local avatar if src is undefined
                  alt={displayName ?? "User Avatar"}
                  width={24}
                  height={24}
                  className="rounded-full"
                  onError={(e) => {
                    e.currentTarget.src = "/default-avatar.png";
                  }} // Fallback on error
                />
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span data-testid="user-name" className="truncate w-full">
                    {displayName}
                  </span>
                  {currentUser?.email && (
                    <span data-testid="user-email" className="text-xs text-muted-foreground truncate w-full">
                      {currentUser.email}
                    </span>
                  )}
                </div>
                <ChevronUp className="ml-auto shrink-0" />
              </SidebarMenuButton>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-testid="user-nav-menu"
            side="top"
            className="w-[--radix-popper-anchor-width]"
          >
            <DropdownMenuItem
              data-testid="user-nav-item-profile"
              className="cursor-pointer"
              onSelect={() => navigate("/profile")}
            >
              Edit Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="user-nav-item-theme"
              className="cursor-pointer"
              onSelect={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {`Toggle ${theme === "light" ? "dark" : "light"} mode`}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild data-testid="user-nav-item-auth">
              <button
                type="button"
                className="w-full cursor-pointer"
                onClick={() => {
                  if (status === "loading") {
                    toast({
                      type: "error",
                      description:
                        "Checking authentication status, please try again!",
                    });
                    return;
                  }

                  if (isGuest) {
                    // For guest, clicking sign out/login should take them to the main login page
                    // to sign in with a permanent account (e.g. Outlook)
                    navigate("/login");
                  } else {
                    signOut();
                    navigate("/login");
                  }
                }}
              >
                {isGuest ? "Login / Sign Up" : "Sign out"}
              </button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
